import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "../../api/axiosInstance";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import useItemDialogFocus from "./useItemDialogFocus";
import useSupplierAccess from "./useSupplierAccess";
import DistributorFormDialog from "./DistributorFormDialog";
import { distributorSearchText, getDistributorTranslator } from "./distributorMessages";
import "./SupplierDistributors.css";

const serializeLinks = (entries) => entries.map((entry) => entry.id ? { id: entry.id }
  : Object.fromEntries(["name", "tax_id", "email", "phone", "address", "aliases"].map((key) => [key, entry[key]])));

function DistributorLinkDialog({ linked, endpoint, access, requiredPermissions, onLink, onClose, portalTarget }) {
  const { themeId, languageId } = usePmsTheme();
  const t = getDistributorTranslator(languageId);
  const id = useId();
  const [query, setQuery] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const dialogRef = useItemDialogFocus(true, false, onClose);
  useEffect(() => {
    let cancelled = false;
    setCatalog([]);
    if (!access.can(requiredPermissions, true)) { setLoading(false); return undefined; }
    const stamp = access.capture();
    setLoading(true); setError("");
    axios.get(endpoint, access.requestConfig(requiredPermissions)).then(({ data }) => {
      if (!Array.isArray(data)) throw new Error("Invalid distributors response");
      if (!cancelled && access.isCurrent(stamp, requiredPermissions)) setCatalog(data);
    }).catch(() => { if (!cancelled) setError("Distributors could not be loaded. Try again."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, retry, access]);
  const available = catalog.filter((entry, index) => entry.id && entry.is_active !== false && entry.is_active !== 0
    && !linked.some((existing) => String(existing.id) === String(entry.id))
    && catalog.findIndex((other) => String(other.id) === String(entry.id)) === index);
  const terms = distributorSearchText(query).split(/\s+/).filter(Boolean);
  const filtered = available.filter((entry) => terms.every((term) => distributorSearchText([entry.name, entry.tax_id, ...(entry.aliases || [])].join(" ")).includes(term)));
  return createPortal(<div className="pilcore-purchasing-overlay dist-overlay" data-theme={themeId} onMouseDown={(event) => {
    event.stopPropagation(); if (event.target === event.currentTarget) onClose();
  }}>
    <section className="dist-dialog" role="dialog" aria-modal="true" aria-labelledby={`${id}-title`} ref={dialogRef} tabIndex={-1}>
      <header><h2 id={`${id}-title`}>{t("Link existing distributor")}</h2><button className="dist-button" type="button" onClick={onClose}>{t("Close")}</button></header>
      <div className="dist-dialog-body">
        <label className="dist-field" htmlFor={`${id}-search`}><span>{t("Search distributors")}</span><input id={`${id}-search`} type="search" value={query} onChange={(event) => setQuery(event.target.value)} /></label>
        {loading && <p role="status">{t("Loading distributors…")}</p>}
        {error && <div className="dist-error" role="alert">{t(error)} <button className="dist-button" type="button" onClick={() => setRetry((value) => value + 1)}>{t("Try again")}</button></div>}
        {!loading && !error && <div className="dist-option-grid">{filtered.map((entry) => <button className="dist-option" type="button" key={entry.id} onClick={() => { if (access.can(requiredPermissions, true)) { onLink(entry); onClose(); } }}>
          <span><strong>{entry.name}</strong>{entry.tax_id && <small>{entry.tax_id}</small>}
            {entry.aliases?.length > 0 && <small>{entry.aliases.join(" · ")}</small>}
            {entry.suppliers?.length > 0 && <small>{t("Linked suppliers: {names}", { names: entry.suppliers.map((supplier) => supplier.name).join(", ") })}</small>}
          </span>
        </button>)}</div>}
        {!loading && !error && !filtered.length && <p>{t(available.length ? "No distributors match your search." : "No other distributors available.")}</p>}
      </div>
    </section>
  </div>, portalTarget || document.body);
}

/** Link mutations stay local until the supplier form sends the complete replacement list. */
export default function SupplierDistributorsEditor({ supplier, businessUnitId, disabled = false, canCreate = false, onChange, onDialogChange, portalTarget }) {
  const { languageId } = usePmsTheme();
  const access = useSupplierAccess(businessUnitId);
  const requiredPermissions = useMemo(() => supplier?.id ? ["purchasing.suppliers.create", "purchasing.suppliers.update"] : ["purchasing.suppliers.create"], [supplier?.id]);
  const editPermissions = ["purchasing.suppliers.create", "purchasing.suppliers.update"];
  const allowed = !disabled && access.can(requiredPermissions, true);
  const t = getDistributorTranslator(languageId);
  const [expanded, setExpanded] = useState(false);
  const [linked, setLinked] = useState(() => supplier?.distributors || []);
  const [loaded, setLoaded] = useState(() => !supplier?.id || Array.isArray(supplier?.distributors));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [draft, setDraft] = useState(null);
  const [linking, setLinking] = useState(false);
  const draftSequence = useRef(0);
  const detailRequest = useRef(0);
  const retryDetail = useRef(null);
  const endpoint = "/api/purchasing/distributors";
  useEffect(() => {
    if (!expanded || loaded || !allowed) return undefined;
    let cancelled = false;
    const stamp = access.capture(), config = access.requestConfig(requiredPermissions);
    setLoading(true); setError("");
    axios.get(endpoint, { ...config, params: { ...config.params, supplier_id: supplier.id } }).then(({ data }) => {
      if (!Array.isArray(data)) throw new Error("Invalid distributors response");
      if (!cancelled && access.isCurrent(stamp, requiredPermissions)) { setLinked(data); setLoaded(true); }
    }).catch(() => { if (!cancelled) setError("Distributors could not be loaded. Try again."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, loaded, allowed, endpoint, supplier?.id, retry, access]);
  const childOpen = Boolean(draft || linking);
  useEffect(() => { onDialogChange?.(childOpen); }, [childOpen, onDialogChange]);
  useEffect(() => () => onDialogChange?.(false), [onDialogChange]);
  const replace = useCallback((entries) => { if (!disabled && access.can(requiredPermissions, true)) { setLinked(entries); onChange(serializeLinks(entries)); } }, [onChange, disabled, access, requiredPermissions]);
  const openDistributor = async (entry) => {
    if (!allowed || !access.can(entry.id ? editPermissions : requiredPermissions, true)) return;
    if (!entry.id) { setDraft(entry); return; }
    retryDetail.current = entry;
    const request = ++detailRequest.current, stamp = access.capture();
    const current = () => request === detailRequest.current && access.isCurrent(stamp, editPermissions);
    setLoading(true); setError("");
    try {
      const { data } = await axios.get(`${endpoint}/${entry.id}`, access.requestConfig(editPermissions));
      if (!current()) return;
      if (!data || Number(data.id) !== Number(entry.id)) throw new Error("Invalid distributor response");
      retryDetail.current = null; setDraft(data);
    } catch (_error) { if (current()) setError("Distributors could not be loaded. Try again."); }
    finally { if (current()) setLoading(false); }
  };
  const saveDistributor = async (payload) => {
    if (!allowed || !access.can(draft?.id ? editPermissions : requiredPermissions, true) || (!draft.id && !canCreate)) throw new Error("Distributor editing is unavailable");
    if (draft.id) {
      const stamp = access.capture();
      const { data } = await axios.patch(`/api/purchasing/distributors/${draft.id}`, payload, access.requestConfig(editPermissions));
      if (!access.isCurrent(stamp, editPermissions)) throw new Error("Distributor editing is unavailable");
      setLinked((previous) => previous.map((entry) => String(entry.id) === String(draft.id) ? { ...entry, ...data, id: draft.id } : entry));
      return;
    }
    const next = { ...payload, __draftId: draft.__draftId || `draft-${++draftSequence.current}` };
    replace(draft.__draftId ? linked.map((entry) => entry.__draftId === draft.__draftId ? next : entry) : [...linked, next]);
  };
  const controlsDisabled = !allowed || loading || !loaded;
  const dialogTarget = portalTarget?.current || portalTarget;
  return <>
    <details className="supplier-distributors" open={expanded} onToggle={(event) => setExpanded(event.currentTarget.open)}>
      <summary>{t("Distributors")}{loaded ? ` (${linked.length})` : ""}</summary>
      {expanded && <>
        <p>{t("These companies deliver goods for this supplier. They remain separate from the supplier catalog.")}</p>
        <p>{t("New distributors and link changes are saved with the supplier.")}</p>
        {loading && <p role="status">{t("Loading distributors…")}</p>}
        {error && <div className="dist-error" role="alert">{t(error)} <button className="dist-button" type="button" disabled={disabled || loading} onClick={() => retryDetail.current ? openDistributor(retryDetail.current) : setRetry((value) => value + 1)}>{t("Try again")}</button></div>}
        {loaded && <div className="dist-linked-list">{linked.map((entry) => <article className="dist-linked-card" key={entry.id || entry.__draftId}>
          <div><strong>{entry.name}</strong>{entry.tax_id && <small>{entry.tax_id}</small>}
            {!entry.id && <small>{t("New distributor, pending save")}</small>}
            {(entry.is_active === false || entry.is_active === 0) && <small>{t("Inactive")}</small>}
            {entry.aliases?.length > 0 && <small>{entry.aliases.join(" · ")}</small>}
          </div>
          <div className="dist-actions">
            <button className="dist-button" type="button" disabled={controlsDisabled || (entry.id && !access.can(editPermissions, true))} onClick={() => openDistributor(entry)}>{t(entry.id ? "Edit distributor" : "Edit draft")}</button>
            <button className="dist-button" type="button" disabled={controlsDisabled} onClick={() => replace(linked.filter((candidate) => candidate !== entry))}>{t("Unlink")}</button>
          </div>
        </article>)}</div>}
        {loaded && !linked.length && <p>{t("No distributors linked yet.")}</p>}
        <div className="dist-actions">{canCreate && <button className="dist-button" type="button" disabled={controlsDisabled} onClick={() => setDraft({})}>{t("Add distributor")}</button>}
          <button className="dist-button" type="button" disabled={controlsDisabled} onClick={() => setLinking(true)}>{t("Link existing distributor")}</button></div>
        <p>{t("Unlinking only removes this supplier's association; the distributor and its other suppliers are kept.")}</p>
      </>}
    </details>
    {draft && allowed && <DistributorFormDialog isOpen initialDistributor={draft.id || draft.__draftId ? draft : null} showSupplierSelection={false}
      description={draft.id ? "Changes to this distributor are saved immediately and apply to all linked suppliers." : undefined}
      onSave={saveDistributor} onClose={() => setDraft(null)} portalTarget={dialogTarget} saveLabel={draft.id ? "Save distributor" : draft.__draftId ? "Save draft" : "Add to supplier"} />}
    {linking && allowed && <DistributorLinkDialog linked={linked} endpoint={`${endpoint}/options`} access={access} requiredPermissions={requiredPermissions} onLink={(entry) => {
      if (!linked.some((existing) => String(existing.id) === String(entry.id))) replace([...linked, entry]);
    }} onClose={() => setLinking(false)} portalTarget={dialogTarget} />}
  </>;
}
